import { useEffect, useState } from 'react'

import { type Learner } from '@/platform/learners/api'

import { DemoBadge } from '../learning/DemoBadge'
import { useFocusOnMount } from '../learning/focus'
import { type DemoRunReport } from '../materials/model'
import {
  type LearningInsight,
  type LearningInsightPreview,
  type LoadInsightPreview,
} from './insight'
import { type LoadMaterialActivity, MaterialActivityView } from './MaterialActivityView'
import { MaterialRunPreview } from './MaterialRunPreview'

/**
 * "Học tập" section of Parent Home (Slice 1 product shell, D-14.4).
 *
 * Real learning data: there is no parent-facing evidence backend yet, so the
 * real section says so for the family's own learners instead of showing
 * numbers. Below it, only when `demoInsight` is provided (local/development,
 * D-02), a DEMO preview of the intended Learning Insight with a fictional
 * learner, clearly separated and labelled.
 */

export const DEMO_HIDDEN_NOTE =
  'Dữ liệu mẫu (DEMO) đã được ẩn vì gia đình đã có kết quả học thật, để không nhầm với kết quả của bé.'

export const DEMO_NOTE =
  'Dữ liệu mẫu để xem trước giao diện. Không phải kết quả học tập của bé nào trong gia đình.'

const STATUS: Readonly<
  Record<LearningInsight['status'], { text: string; tone: string }>
> = {
  difficulty_suggested: {
    text: 'Có thể cần hỗ trợ',
    tone: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  },
  insufficient_evidence: {
    text: 'Chưa đủ dữ liệu',
    tone: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
  },
  no_difficulty_observed: {
    text: 'Chưa thấy khó khăn',
    tone: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
  },
}

export interface LearningOverviewProps {
  readonly learners: readonly Learner[]
  readonly demoInsight: LoadInsightPreview | null
  /** Show the DEMO school-material preview (local/development only). */
  readonly demoMaterialsEnabled?: boolean
  /** Last DEMO school-material run on this device, memory only. */
  readonly demoRun?: DemoRunReport | null
  /** Real persisted learning evidence (D-17); absent where no backend exists. */
  readonly loadMaterialActivity?: LoadMaterialActivity | null
}

type DemoState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; preview: LearningInsightPreview }

export function LearningOverview({
  learners,
  demoInsight,
  demoMaterialsEnabled = false,
  demoRun = null,
  loadMaterialActivity = null,
}: LearningOverviewProps): React.JSX.Element {
  const heading = useFocusOnMount<HTMLHeadingElement>()
  // Real evidence exists: DEMO previews are hidden so a parent cannot mistake
  // a sample insight (e.g. a sample fractions difficulty) for their child's.
  const [hasRealEvidence, setHasRealEvidence] = useState(false)

  return (
    <div className="flex flex-col gap-6">
      <h1 ref={heading} tabIndex={-1} className="text-2xl font-semibold">
        Tình hình học tập
      </h1>

      <section
        aria-labelledby="learning-real"
        className="flex flex-col gap-2 rounded-2xl border border-slate-200 p-4 dark:border-slate-800"
      >
        <h2 id="learning-real" className="text-lg font-semibold">
          Của các bé trong gia đình
        </h2>
        {loadMaterialActivity === null ? (
          <p className="text-slate-700 dark:text-slate-300">
            Chưa có báo cáo học tập. Phiên bản này chưa tổng hợp bài làm của các bé cho
            phụ huynh, nên chưa có nhận định hay đề xuất nào.
          </p>
        ) : (
          <MaterialActivityView
            learners={learners}
            load={loadMaterialActivity}
            onLoaded={setHasRealEvidence}
          />
        )}
        {learners.length > 0 && (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Hồ sơ: {learners.map((l) => l.nickname).join(', ')}
          </p>
        )}
      </section>

      {hasRealEvidence ? (
        (demoMaterialsEnabled || demoInsight !== null) && (
          <p className="text-sm text-slate-600 dark:text-slate-400">{DEMO_HIDDEN_NOTE}</p>
        )
      ) : (
        <>
          {demoMaterialsEnabled && <MaterialRunPreview run={demoRun} />}
          {demoInsight !== null && <DemoPreview load={demoInsight} />}
        </>
      )}
    </div>
  )
}

function DemoPreview({ load }: { load: LoadInsightPreview }): React.JSX.Element {
  const [state, setState] = useState<DemoState>({ kind: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let active = true
    load().then(
      (preview) => {
        if (active) setState({ kind: 'ready', preview })
      },
      () => {
        if (active) setState({ kind: 'error' })
      },
    )
    return () => {
      active = false
    }
  }, [load, reloadKey])

  return (
    <section
      aria-labelledby="learning-demo"
      className="flex flex-col gap-5 rounded-2xl border-2 border-dashed border-fuchsia-300 p-4 dark:border-fuchsia-800"
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <DemoBadge />
          <h2 id="learning-demo" className="text-lg font-semibold">
            Xem trước: nhận định học tập
          </h2>
        </div>
        <p className="text-slate-700 dark:text-slate-300">{DEMO_NOTE}</p>
        {state.kind === 'ready' && (
          <p>
            <span className="font-semibold">Hồ sơ mẫu: </span>
            {state.preview.learnerName}, không phải bé trong gia đình
          </p>
        )}
      </div>

      {state.kind === 'loading' && <p role="status">Đang tải dữ liệu mẫu…</p>}
      {state.kind === 'error' && (
        <div className="flex flex-col items-start gap-2">
          <p role="alert">Không tải được dữ liệu mẫu.</p>
          <button
            type="button"
            className="h-11 rounded-xl border border-slate-300 px-4 dark:border-slate-700"
            onClick={() => {
              setState({ kind: 'loading' })
              setReloadKey((k) => k + 1)
            }}
          >
            Thử lại
          </button>
        </div>
      )}
      {state.kind === 'ready' && <DemoContent preview={state.preview} />}
    </section>
  )
}

function DemoContent({
  preview,
}: {
  preview: LearningInsightPreview
}): React.JSX.Element {
  return (
    <>
      <section aria-labelledby="demo-activity" className="flex flex-col gap-2">
        <h3 id="demo-activity" className="font-semibold">
          Tổng quan — {preview.periodLabel}
        </h3>
        <ul className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {preview.activity.map((a) => (
            <li key={a.subject} className="rounded-xl bg-slate-50 p-3 dark:bg-slate-900">
              <p className="font-semibold">{a.subject}</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {a.sessions} buổi · {a.minutes} phút
              </p>
            </li>
          ))}
        </ul>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Số buổi và thời gian chỉ cho biết bé đã học, không chứng minh bé đã nắm bài.
        </p>
      </section>

      <section aria-labelledby="demo-insights" className="flex flex-col gap-3">
        <h3 id="demo-insights" className="font-semibold">
          Nhận định
        </h3>
        <ul className="grid gap-3 md:grid-cols-2">
          {preview.insights.map((insight) => (
            <InsightCard key={insight.id} insight={insight} />
          ))}
        </ul>
      </section>

      <section aria-labelledby="demo-actions" className="flex flex-col gap-2">
        <h3 id="demo-actions" className="font-semibold">
          Việc nên làm tiếp
        </h3>
        <ol className="flex flex-col gap-2">
          {preview.actions.map((a) => (
            <li key={a.id} className="rounded-xl bg-sky-50 p-3 dark:bg-sky-950/40">
              <p>{a.action}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Vì: {a.reason}</p>
            </li>
          ))}
        </ol>
      </section>
    </>
  )
}

function InsightCard({ insight }: { insight: LearningInsight }): React.JSX.Element {
  const [showEvidence, setShowEvidence] = useState(false)
  const status = STATUS[insight.status]
  const evidenceId = `evidence-${insight.id}`

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">
          {insight.subject} · {insight.topic}
        </p>
        <span className={`rounded-full px-2 py-0.5 text-sm font-semibold ${status.tone}`}>
          {status.text}
        </span>
      </div>
      <p>
        <span className="font-semibold">Quan sát: </span>
        {insight.observed}
      </p>
      <p className="text-sm text-slate-700 dark:text-slate-300">
        Dựa trên {insight.sampleCount} câu trả lời.
        {insight.status === 'insufficient_evidence' && ' Chưa đủ để kết luận.'}
      </p>
      {insight.interpretation !== null && (
        <p className="rounded-lg bg-slate-50 p-2 text-sm dark:bg-slate-900">
          <span className="font-semibold">Diễn giải (mẫu, không do AI tạo): </span>
          {insight.interpretation}
        </p>
      )}
      <button
        type="button"
        aria-expanded={showEvidence}
        aria-controls={evidenceId}
        className="self-start px-1 text-sky-800 underline dark:text-sky-300"
        onClick={() => {
          setShowEvidence((v) => !v)
        }}
      >
        {showEvidence
          ? 'Ẩn bằng chứng'
          : `Xem bằng chứng (${String(insight.evidence.length)})`}
      </button>
      <ul id={evidenceId} hidden={!showEvidence} className="flex flex-col gap-1 text-sm">
        {insight.evidence.map((e) => (
          <li key={`${e.source}-${e.detail}`}>
            <span className="font-semibold">{e.source}: </span>
            {e.detail}
          </li>
        ))}
      </ul>
    </li>
  )
}
